import { useState } from 'react';
import { useGT } from 'gt-react';
import type { WorkbookEditor } from './useWorkbookEditor';
import { isSpreadsheetFile, uploadReference } from './workbookUpload';

/** A file being uploaded: shown at once, replaced by its attachment on success. */
export interface PendingUpload {
  id: string;
  name: string;
}

/**
 * Uploads spreadsheet files to the Workbook (an Excel workbook as is, a CSV
 * converted to one), tracking the uploads in flight: local UI state, never
 * part of the saved Definition. A finished upload opens its File.
 */
export function useWorkbookUploads(
  editor: Pick<WorkbookEditor, 'workbookId' | 'edits' | 'setError'>,
  openItem: (id: string) => void,
) {
  const gt = useGT();
  const [pending, setPending] = useState<readonly PendingUpload[]>([]);
  async function uploadOne(file: File) {
    const pendingId = crypto.randomUUID();
    setPending((current) => [...current, { id: pendingId, name: file.name }]);
    try {
      const added = await uploadReference(editor, file);
      openItem(added.id);
    } catch (error) {
      editor.setError(
        error instanceof Error
          ? error.message
          : gt('Workbook action failed.', { context: 'workbook action error' }),
      );
    }
    setPending((current) =>
      current.filter((upload) => upload.id !== pendingId),
    );
  }
  return {
    pending,
    /** Uploads each spreadsheet of `files`; anything else is refused with a message. */
    upload: async (files: readonly File[]) => {
      editor.setError(null);
      const spreadsheets = files.filter(isSpreadsheetFile);
      if (spreadsheets.length < files.length)
        editor.setError(
          gt('Only Excel (.xlsx) and CSV files can be added.', {
            context: 'workbook upload refused file type',
          }),
        );
      await Promise.all(spreadsheets.map(uploadOne));
    },
  };
}

export type WorkbookUploads = ReturnType<typeof useWorkbookUploads>;
